import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ShipmentStatus } from '@/features/delivery/shipments/types';
import { type ApiError, apiRequest } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * « Confirmer et envoyer à Jibex » — mirrors `apps/api/src/modules/delivery/order-send.service.ts`
 * (`GET/POST /merchant/deliveries/orders/:orderId/{jibex-form,send}`).
 *
 * The form copies Jibex's own « Nouveau colis » screen. The API pre-fills it from the order, says which required
 * fields the order lacks, and — once sent — reports the parcel's state; the actual send is the existing one (the order
 * becomes « Préparée », the dispatch job creates the parcel, never twice).
 */

export const PARCEL_TYPES = ['FIX', 'ONP', 'BLK', 'SMD'] as const;
export type ParcelType = (typeof PARCEL_TYPES)[number];

export type RequiredField =
  'recipientName' | 'phone' | 'addressLine' | 'governorate' | 'delegation';

export interface ParcelDetails {
  readonly parcelType: ParcelType;
  readonly weight: number;
  readonly pieces: number;
  readonly fragile: boolean;
  readonly openingAllowed: boolean;
  readonly exchange: boolean;
  readonly comment: string | null;
  readonly phone2: string | null;
}

export interface SendForm {
  readonly carrierConnected: boolean;
  readonly orderNumber: string;
  readonly orderStatus: string;
  readonly sendable: boolean;
  readonly values: {
    readonly recipientName: string;
    readonly phone: string;
    readonly addressLine: string;
    readonly governorate: string;
    readonly delegation: string;
    readonly codAmount: number;
    readonly designation: string;
    readonly parcel: ParcelDetails;
  };
  readonly missing: readonly RequiredField[];
  readonly shipment: {
    readonly id: string;
    readonly status: ShipmentStatus;
    readonly trackingNumber: string | null;
    readonly lastError: string | null;
  } | null;
}

/** What the merchant edits — strings, as typed; numbers are parsed by `validateSendValues`. */
export interface SendValues {
  readonly recipientName: string;
  readonly phone: string;
  readonly phone2: string;
  readonly addressLine: string;
  readonly governorate: string;
  readonly delegation: string;
  readonly comment: string;
  readonly parcelType: ParcelType;
  readonly weight: string;
  readonly pieces: string;
  readonly fragile: boolean;
  readonly openingAllowed: boolean;
  readonly exchange: boolean;
}

export interface SendBody {
  readonly recipientName: string;
  readonly phone: string;
  readonly phone2: string | null;
  readonly addressLine: string;
  readonly governorate: string;
  readonly delegation: string;
  readonly comment: string | null;
  readonly parcelType: ParcelType;
  readonly weight: number;
  readonly pieces: number;
  readonly fragile: boolean;
  readonly openingAllowed: boolean;
  readonly exchange: boolean;
}

export type SendFieldError =
  'required' | 'phoneInvalid' | 'phone2Invalid' | 'weightInvalid' | 'piecesInvalid';

export function initialValues(form: SendForm): SendValues {
  const { values } = form;
  return {
    recipientName: values.recipientName,
    phone: values.phone,
    phone2: values.parcel.phone2 ?? '',
    addressLine: values.addressLine,
    governorate: values.governorate,
    delegation: values.delegation,
    comment: values.parcel.comment ?? '',
    parcelType: values.parcel.parcelType,
    weight: String(values.parcel.weight),
    pieces: String(values.parcel.pieces),
    fragile: values.parcel.fragile,
    openingAllowed: values.parcel.openingAllowed,
    exchange: values.parcel.exchange,
  };
}

/** The same Tunisian phone rule the API applies (8 digits, `+216` optional, not starting with 0 or 1). */
export function normaliseTunisianPhone(raw: string): string | null {
  let digits = raw.trim().replace(/[\s.\-()]/gu, '');
  if (digits.startsWith('+216')) digits = digits.slice(4);
  else if (digits.startsWith('00216')) digits = digits.slice(5);
  else if (digits.startsWith('216') && digits.length === 11) digits = digits.slice(3);
  return /^[2-9][0-9]{7}$/u.test(digits) ? digits : null;
}

/**
 * Checks every field the way the API will; nothing is sent while an error remains. Returns the body to send, or the
 * error of each field.
 */
export function validateSendValues(
  values: SendValues,
):
  | { readonly body: SendBody }
  | { readonly errors: Partial<Record<keyof SendValues, SendFieldError>> } {
  const errors: Partial<Record<keyof SendValues, SendFieldError>> = {};
  const required = ['recipientName', 'phone', 'addressLine', 'governorate', 'delegation'] as const;
  for (const key of required) if (values[key].trim() === '') errors[key] = 'required';
  if (errors.phone === undefined && normaliseTunisianPhone(values.phone) === null)
    errors.phone = 'phoneInvalid';
  if (values.phone2.trim() !== '' && normaliseTunisianPhone(values.phone2) === null)
    errors.phone2 = 'phone2Invalid';
  const weight = Number(values.weight.replace(',', '.'));
  if (values.weight.trim() === '' || !Number.isFinite(weight) || weight <= 0 || weight > 1_000)
    errors.weight = 'weightInvalid';
  const pieces = Number(values.pieces);
  if (!Number.isInteger(pieces) || pieces < 1 || pieces > 999) errors.pieces = 'piecesInvalid';
  if (Object.keys(errors).length > 0) return { errors };
  return {
    body: {
      recipientName: values.recipientName.trim(),
      phone: normaliseTunisianPhone(values.phone) ?? values.phone,
      phone2: values.phone2.trim() === '' ? null : normaliseTunisianPhone(values.phone2),
      addressLine: values.addressLine.trim(),
      governorate: values.governorate,
      delegation: values.delegation.trim(),
      comment: values.comment.trim() === '' ? null : values.comment.trim(),
      parcelType: values.parcelType,
      weight,
      pieces,
      fragile: values.fragile,
      openingAllowed: values.openingAllowed,
      exchange: values.exchange,
    },
  };
}

const formKey = (shopId: string | null, orderId: string | null): readonly unknown[] => [
  'jibex-send-form',
  shopId,
  orderId,
];

/**
 * The form for one order. `follow` polls every 2 s while the parcel is being created (after a send), so the outcome —
 * created with its tracking number, or refused with its code — appears without any click.
 */
export function useSendFormQuery(
  orderId: string | null,
  follow: boolean,
): UseQueryResult<SendForm, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: formKey(activeShopId, orderId),
    queryFn: () =>
      apiRequest<SendForm>(`/merchant/deliveries/orders/${orderId ?? ''}/jibex-form`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null && orderId !== null,
    staleTime: 0,
    refetchInterval: follow ? 2_000 : false,
  });
}

export function useSendToJibexMutation(): UseMutationResult<
  SendForm,
  ApiError,
  { readonly orderId: string; readonly body: SendBody }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, body }) =>
      apiRequest<SendForm>(`/merchant/deliveries/orders/${orderId}/send`, {
        method: 'POST',
        body,
        accessToken,
        shopId: activeShopId,
      }),
    onSuccess: (form, { orderId }) => {
      queryClient.setQueryData(formKey(activeShopId, orderId), form);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      await queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
}
